import {
  GenericElement,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { RoleSubjectEntity } from '@agilelab/plugin-wb-rbac-common';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAsyncFn } from 'react-use';
import { rbacApiRef } from '../../../../plugin';
import { useAssignmentsContext } from '../AssignmentsContext';
import { AssignmentFormType } from './types';

export interface Filters {
  text?: string;
}
export interface ManageAssignmentsDrawerContextProviderProps {
  children?: ReactNode;
}
export interface ManageAssignmentsDrawerContextType
  extends ManageAssignmentsDrawerContextProviderProps {
  addAssignments: (
    assignmentSubjects: AssignmentFormType,
    upsert?: boolean,
  ) => Promise<RoleSubjectEntity[]>;

  selectedScopes: GenericElement[];
  setSelectedScopes: Dispatch<SetStateAction<GenericElement[]>>;

  formValue: AssignmentFormType | undefined;
  setFormValue: Dispatch<SetStateAction<AssignmentFormType | undefined>>;

  selectedKind: string;
  setSelectedKind: Dispatch<SetStateAction<string>>;

  syncUserScopes: (assignmentSubjects: AssignmentFormType) => Promise<any>;
}

export const ManageAssignmentsDrawerContext =
  createContext<ManageAssignmentsDrawerContextType>(
    {} as ManageAssignmentsDrawerContextType,
  );

export const ManageAssignmentsDrawerContextProvider = ({
  children,
}: ManageAssignmentsDrawerContextProviderProps) => {
  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);

  const {
    loadAssignments,
    roleId,
    setOpenManageDrawer,
    selectedRow,
    setSelectedRow,
  } = useAssignmentsContext();

  const [formValue, setFormValue] = useState<AssignmentFormType>();

  const [selectedScopes, setSelectedScopes] = useState<GenericElement[]>([]);

  const [selectedKind, setSelectedKind] = useState<string>('System');

  useEffect(() => {
    if (selectedRow) {
      setFormValue({
        users: [selectedRow.userOrGroup.urn],
        scopes: selectedRow.scopes.map(s => s.urn),
      });
    } else {
      setFormValue({
        scopes: ['*'],
      });
    }
  }, [selectedRow]);

  const [_, addAssignments] = useAsyncFn(
    async ({ users = [], scopes }: AssignmentFormType) => {
      try {
        const token = (await identityApi.getCredentials()).token;

        const rolesSubjects = users.flatMap(user =>
          scopes.map(scope => ({
            subject: user,
            roleId: roleId,
            entityRef: scope,
            enabled: true,
          })),
        );

        const results = await rbacApi.addRolesSubjects({
          rolesSubjects,
          options: { token },
        });

        setOpenManageDrawer(false);
        loadAssignments();

        alertApi.post({
          message: `Successfully added ${results.length} assignment(s).`,
          severity: results.length ? 'success' : 'warning',
        });

        setSelectedRow(undefined);
        return results;
      } catch (e) {
        alertApi.post({
          message: e || 'An error occurred while adding assignments.',
          severity: 'error',
        });
        return e;
      }
    },
  );

  const syncUserScopes = async ({
    users = [],
    scopes,
  }: {
    users?: string[];
    scopes: string[];
  }) => {
    try {
      const token = (await identityApi.getCredentials()).token;

      const scopesToAdd = scopes.filter(
        scope => !formValue?.scopes.includes(scope),
      );
      const scopesToRemove =
        formValue?.scopes.filter(scope => !scopes.includes(scope)) ?? [];

      const result = await rbacApi.syncUserScopes({
        syncData: {
          users,
          roleId,
          scopesToAdd,
          scopesToRemove,
        },
        options: {
          token,
          upsert: true,
        },
      });

      if (result.errors.length > 0) {
        alertApi.post({
          message: `Sync failed: ${result.errors[0]}`,
          severity: 'error',
        });
      } else {
        alertApi.post({
          message: `Scopes synced successfully. Added: ${result.added.length}, Removed: ${result.removed.length}`,
          severity: 'success',
        });
      }

      setOpenManageDrawer(false);
      loadAssignments();
      return result;
    } catch (e: any) {
      setOpenManageDrawer(false);
      alertApi.post({
        message: `${e.message}` || 'An error occurred while syncing scopes.',
        severity: 'error',
      });
      return { added: [], removed: [], errors: [e.message] };
    } finally {
      setSelectedRow(undefined);
    }
  };

  return (
    <ManageAssignmentsDrawerContext.Provider
      value={{
        formValue,
        setFormValue,

        selectedScopes,
        setSelectedScopes,

        selectedKind,
        setSelectedKind,

        addAssignments,

        syncUserScopes,
      }}
    >
      {children}
    </ManageAssignmentsDrawerContext.Provider>
  );
};

export const useManageAssignmentsDrawerContext = () => {
  const context = useContext(ManageAssignmentsDrawerContext);
  if (!context) {
    throw new Error(
      'useManageAssignmentsDrawerContext must be used within a ManageAssignmentsDrawerContextProvider',
    );
  }
  return context as ManageAssignmentsDrawerContextType;
};
